import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useModulePermission } from '../../hooks/usePermission';
import { FM130, FM131, M203SaveRequest, m203Api } from '../../services/m203Api';
import { BatchAcceptanceBridge, pwm203f1AcceptanceManifest } from '../../features/acceptance';

const emptyHeader = (): FM130 => ({
  fm130_11: dayjs().format('YYYY-MM-DD'), fm130_13: '', fm130_16: '',
  fm130_17: true, fm130_18: 'NTD', fm130_19: 1, fm130_20: '', fm130_22: '', fm130_23: false, fm130_231: 4,
  fm130_80: 'N',
});
const emptyDetail = (): FM131 => ({ fm131_11: '', fm131_12: '', fm131_121: '', fm131_13: '', fm131_15: 0, fm131_17: 0 });

const M203Form: React.FC = () => {
  const permission = useModulePermission('pwm203f1');
  const [rows, setRows] = useState<FM130[]>([]);
  const [master, setMaster] = useState<FM130>(emptyHeader());
  const [details, setDetails] = useState<FM131[]>([emptyDetail()]);
  const [mode, setMode] = useState<'browse' | 'add' | 'edit'>('browse');
  const [busy, setBusy] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);
  const locked = master.fm130_80 === 'P';
  const creatorLocked = master.fm130_15?.trim() === '慶佑';
  const editable = mode !== 'browse' && !locked && !creatorLocked;

  const reload = useCallback(async () => {
    const result = await m203Api.list();
    setRows(result.data);
  }, []);
  useEffect(() => { reload().catch(() => message.error('交運單清單載入失敗')); }, [reload]);

  const selectRecord = async (id?: string) => {
    if (!id) return;
    setBusy(true);
    try {
      const row = await m203Api.get(id);
      setLastApiResponse(row);
      setMaster(row); setDetails(row.details || []); setMode('browse');
    } catch { message.error('交運單載入失敗'); } finally { setBusy(false); }
  };
  const setField = <K extends keyof FM130>(key: K, value: FM130[K]) => setMaster(prev => ({ ...prev, [key]: value }));
  const setDetail = (index: number, patch: Partial<FM131>) => setDetails(prev => prev.map((row, i) => i === index ? { ...row, ...patch } : row));

  const save = async () => {
    if (locked) { message.error('已核准交運單不可修改'); return; }
    const request: M203SaveRequest = { master, details };
    setBusy(true);
    try {
      const result = mode === 'add' ? await m203Api.create(request) : await m203Api.update(master.fm130_01!, request);
      setLastApiResponse(result);
      setMaster(result); setDetails(result.details || []); setMode('browse'); await reload();
      message.success('儲存完成');
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      message.error(Array.isArray(errors) ? errors.map((x: any) => x.message).join('；') : '儲存失敗');
    } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!master.fm130_01) return;
    setBusy(true);
    try {
      const draft = await m203Api.copy(master.fm130_01);
      setLastApiResponse(draft);
      setMaster(draft.master); setDetails(draft.details); setMode('add');
    } catch { message.error('複製失敗'); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!master.fm130_01 || locked) return;
    setBusy(true);
    try {
      await m203Api.delete(master.fm130_01);
      setLastApiResponse({ deleted: master.fm130_01 });
      setMaster(emptyHeader()); setDetails([emptyDetail()]); setMode('browse'); await reload();
      message.success('刪除成功');
    } catch { message.error('刪除失敗'); } finally { setBusy(false); }
  };

  const detailColumns = useMemo(() => [
    { title: <span data-contract-field="fm131_11">庫位</span>, dataIndex: 'fm131_11', render: (_: string, row: FM131, i: number) => <Input disabled={!editable} value={row.fm131_11} onChange={e => setDetail(i, { fm131_11: e.target.value })} /> },
    { title: <span data-contract-field="fm131_12">產品(前)</span>, dataIndex: 'fm131_12', render: (_: string, row: FM131, i: number) => <Input disabled={!editable} value={row.fm131_12} onChange={e => setDetail(i, { fm131_12: e.target.value })} /> },
    { title: <span data-contract-field="fm131_121">產品(後)</span>, dataIndex: 'fm131_121', render: (_: string, row: FM131, i: number) => <Input disabled={!editable} value={row.fm131_121} onChange={e => setDetail(i, { fm131_121: e.target.value })} /> },
    { title: <span data-contract-field="fm131_13">批號</span>, dataIndex: 'fm131_13', render: (_: string, row: FM131, i: number) => <Input disabled={!editable} value={row.fm131_13} onChange={e => setDetail(i, { fm131_13: e.target.value })} /> },
    { title: <span data-contract-field="fm131_15">數量</span>, dataIndex: 'fm131_15', render: (_: number, row: FM131, i: number) => <InputNumber disabled={!editable} value={row.fm131_15} onChange={v => setDetail(i, { fm131_15: Number(v || 0) })} /> },
    { title: <span data-contract-field="fm131_17">單價</span>, dataIndex: 'fm131_17', render: (_: number, row: FM131, i: number) => <InputNumber disabled={!editable} value={row.fm131_17} onChange={v => setDetail(i, { fm131_17: Number(v || 0) })} /> },
    { title: '', key: 'remove', render: (_: unknown, __: FM131, i: number) => <Button danger disabled={!editable || details.length === 1} onClick={() => setDetails(v => v.filter((_, n) => n !== i))}>刪列</Button> },
  ], [editable, details.length]);
  const queryAcceptanceExact = useCallback((id: string) => m203Api.get(id), []);

  if (!permission.canView) return <div>無查詢權限</div>;
  return <div style={{ padding: 20 }}>
    <Space wrap style={{ marginBottom: 16 }}>
      <Popconfirm title="確認刪除這張交運單？" onConfirm={remove}>
        <Button data-acceptance-action="PWM203-ACT-DELETE" danger disabled={!permission.canDelete || !master.fm130_01 || locked || creatorLocked}>刪除</Button>
      </Popconfirm>
      <Select data-acceptance-action="PWM203-ACT-READ" style={{ width: 180 }} placeholder="選擇交運單" value={master.fm130_01} options={rows.map(r => ({ value: r.fm130_01!, label: r.fm130_01! }))} onChange={selectRecord} />
      <Button disabled={!permission.canCreate} onClick={() => { setMaster(emptyHeader()); setDetails([emptyDetail()]); setMode('add'); }}>新增</Button>
      <Button disabled={!permission.canEdit || !master.fm130_01 || locked || creatorLocked} onClick={() => setMode('edit')}>修改</Button>
      <Button data-acceptance-action="PWM203-ACT-COPY" disabled={!permission.canCreate || !master.fm130_01} onClick={copy}>複製</Button>
      {mode === 'add' ? (
        <Button data-acceptance-action="PWM203-ACT-CREATE" type="primary" loading={busy} disabled={!editable} onClick={save}>
          儲存
        </Button>
      ) : (
        <Button data-acceptance-action="PWM203-ACT-UPDATE" type="primary" loading={busy} disabled={!editable} onClick={save}>
          儲存
        </Button>
      )}
      <Button data-acceptance-action="PWM203-ACT-PRINT" disabled={!permission.canPrint || !master.fm130_01} onClick={() => window.print()}>列印</Button>
      <Tag color={locked ? 'green' : 'orange'}>{locked ? 'P 已核准／鎖定' : 'N 未核准'}</Tag>
    </Space>
    <Form layout="vertical">
      <Space wrap align="start">
        <Form.Item label={<span data-contract-field="fm130_17">是否計入應收</span>}><Checkbox disabled={!editable} checked={Boolean(master.fm130_17)} onChange={e => setField('fm130_17', e.target.checked)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_23">產生應收帳款</span>}><Checkbox disabled checked={master.fm130_231 === 1 || master.fm130_231 === 2} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_11">交運日期</span>} required><DatePicker disabled={!editable} value={dayjs(master.fm130_11)} onChange={v => setField('fm130_11', v?.format('YYYY-MM-DD') || '')} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_13">交運單位</span>} required><Input disabled={!editable} value={master.fm130_13} onChange={e => setField('fm130_13', e.target.value)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_16">客戶代號</span>} required><Input disabled={!editable} value={master.fm130_16} onChange={e => setField('fm130_16', e.target.value)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_18">幣別</span>} required><Input disabled={!editable} value={master.fm130_18} onChange={e => setField('fm130_18', e.target.value)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_19">匯率</span>} required><InputNumber disabled={!editable} value={master.fm130_19} onChange={v => setField('fm130_19', Number(v || 0))} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_20">出貨部門</span>} required><Input disabled={!editable} value={master.fm130_20} onChange={e => setField('fm130_20', e.target.value)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_22">通知人員</span>} required><Input disabled={!editable} value={master.fm130_22} onChange={e => setField('fm130_22', e.target.value)} /></Form.Item>
        <Form.Item label={<span data-contract-field="fm130_231">發票種類</span>} required><InputNumber disabled={!editable} value={master.fm130_231} onChange={v => setField('fm130_231', Number(v || 0))} /></Form.Item>
      </Space>
    </Form>
    <Table rowKey={(_, i) => String(i)} pagination={false} dataSource={details} columns={detailColumns} />
    <Button style={{ marginTop: 8 }} disabled={!editable} onClick={() => setDetails(v => [...v, emptyDetail()])}>新增明細</Button>
    <BatchAcceptanceBridge
      manifest={pwm203f1AcceptanceManifest}
      records={rows}
      selectedKeys={master.fm130_01 ? [master.fm130_01] : []}
      keyOf={(item) => item.fm130_01 || ''}
      queryExact={queryAcceptanceExact}
      lastApiResponse={lastApiResponse}
      loading={busy}
      onAutoFill={() => { if (rows[0]?.fm130_01) void selectRecord(rows[0].fm130_01); }}
      autoFillDisabled={rows.length === 0}
      autoFillReason="先讀取一張既有交運單作為安全範例；不會自動新增、修改或送出資料。"
      screenDraft={{ mode, master, details }}
    />
  </div>;
};

export default M203Form;
